import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import moment from 'moment';
import Parser from 'html-react-parser';
import {
  Row,
  Col,
  Button,
  Form,
  FormFeedback,
  FormGroup,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Input,
  Label,
  ModalHeader,
  ModalBody,
  ModalFooter,
  UncontrolledTooltip,
  Popover,
  PopoverBody,
} from 'reactstrap';
import { FormattedMessage, injectIntl } from 'react-intl';

import {
  APIcreateUploadPage,
  APIupdateUploadPage,
} from '../../../api/UploadPages';
import { APIgetUsers } from '../../../api/User';

import FileStatusSelect from '../../Upload/FileStatusSelect';
import ShareModalExpirationDate from '../../Share/ModalExpirationDate';
import ShareModalLinkCopy from '../../Share/ModalLinkCopy';
import ShareModalPasswordProtect from '../../Share/ModalPasswordProtect';
import ShareModalRequireAcceptDocument from '../../Share/ModalRequireAcceptDocument';
import ModalRequireCheckbox from '../../ModalRequireCheckbox';
import CollectionSelectModal from '../../CollectionSelectModal';
import AccessModalSelect from '../../Access/ModalSelect';
import ProcessingSpinner from '../../ProcessingSpinner';

import { parseResponseErrors } from '../../../helpers';
import { userCan } from '../../../helpers/Auth';
import { getSetting } from '../../../helpers/Settings';
import { isValidShareLinkSuffix } from '../../../helpers/Validators';

import ErrorBoundary from '../../ErrorBoundary';

class UploadPageEditModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      title: '',
      description: '',
      collection: null,
      user: null,
      requirePassword: false,
      password: '',
      requireEmail: false,
      requireName: false,
      expirationDate: null,
      url: { prefix: `${getSetting('workspace_url')}/u/`, suffix: '' },
      fileStatus: null,
      saving: false,
      errors: {},
      users: [],
      usersLoading: false,
      collectionSelectOpen: false,
      defaultFilesAccess: 'inherit',
      requireAcceptDocument: false,
      dropdownOpen: false,
      view: null,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleNewCollection = this.handleNewCollection.bind(this);
    this.handleRemoveSelectedCollection = this.handleRemoveSelectedCollection.bind(this);
    this.handleSelectUser = this.handleSelectUser.bind(this);
    this.handleChangeRequireAcceptDocument = this.handleChangeRequireAcceptDocument.bind(this);
    this.changeUrlSuffix = this.changeUrlSuffix.bind(this);
    this.changeExpirationDate = this.changeExpirationDate.bind(this);
    this.openSettings = this.openSettings.bind(this);
    this.openCollectionSelectModal = this.openCollectionSelectModal.bind(this);
    this.closeCollectionSelectModal = this.closeCollectionSelectModal.bind(this);
    this.toggleDropdown = this.toggleDropdown.bind(this);
    this.changeDefaultFilesAccess = this.changeDefaultFilesAccess.bind(this);
    this.resetView = this.resetView.bind(this);
  }

  componentDidMount() {
    this.loadUsers();

    const { uploadPage } = this.props;
    const { url } = this.state;

    if (uploadPage) {
      this.setState({
        title: uploadPage.title,
        description: uploadPage.description,
        collection: uploadPage.collection,
        user: uploadPage.user,
        requirePassword: uploadPage.require_password,
        password: uploadPage.password,
        requireLogin: uploadPage.require_login,
        requireEmail: uploadPage.require_email,
        requireName: uploadPage.require_name,
        expirationDate: uploadPage.expires_at ? moment(uploadPage.expires_at) : null,
        url: uploadPage.url,
        fileStatus: uploadPage.file_status,
        defaultFilesAccess: uploadPage.default_files_access,
        requireAcceptDocument: uploadPage.require_accept_document,
      });
    } else {
      this.setState({
        url: {
          ...url,
          suffix: Math.random().toString(36).substr(2, 8),
        },
      });
    }
  }

  getExpirationText() {
    const { intl } = this.props;
    const { expirationDate } = this.state;
    if (expirationDate) {
      const days = expirationDate.diff(moment(), 'days') + 1;
      return `${intl.formatMessage({ defaultMessage: 'Expires in' })}
          <strong>${intl.formatMessage({ defaultMessage: '{days} {days, plural, one {day} other {days}}.' }, { days })}</strong>`;
    }
    return intl.formatMessage({ defaultMessage: 'Never expires.' });
  }

  getAccessText() {
    const {
      requireLogin, requirePassword, requireEmail, requireName,
    } = this.state;

    let text = <FormattedMessage defaultMessage="Anyone can upload" />;

    if (requireLogin) {
      text = (
        <FormattedMessage
          defaultMessage="Anyone <strong>logged in</strong> can upload"
        />
      );
    }

    const requirements = [];
    if (requirePassword) {
      requirements.push(<FormattedMessage defaultMessage="password" />);
    }
    if (requireEmail) {
      requirements.push(<FormattedMessage defaultMessage="email address" />);
    }
    if (requireName) {
      requirements.push(<FormattedMessage defaultMessage="name" />);
    }

    if (requirements.length > 0) {
      const joinedRequirements = requirements.reduce((acc, curr, idx) => {
        if (idx === 0) return [curr];
        const separator = idx === requirements.length - 1 ? (
          <>
            {' '}
            <FormattedMessage key={curr} defaultMessage="and" />
            {' '}
          </>
        ) : ', ';
        return [...acc, separator, curr];
      }, []);

      text = (
        <FormattedMessage
          defaultMessage="{text}{requirementsCount, select,
            0 {}
            1 {, {requirements} is required}
            other {, {requirements} are required}}"
          values={{
            text,
            requirements: <>{joinedRequirements}</>,
            requirementsCount: requirements.length,
          }}
        />

      );
    }

    return <>{text}.</>;
  }

  openSettings(e) {
    if (e) {
      e.preventDefault();
    }
    this.setState({ view: 'settings' });
  }

  loadUsers() {
    this.setState({ usersLoading: true });

    APIgetUsers({ all: true }).then((response) => {
      const { data } = response;
      if (data) {
        this.setState({ users: data });
      }
      this.setState({ usersLoading: false });
    });
  }

  validateForm() {
    const { intl } = this.props;
    const {
      url,
      title,
      requirePassword,
      password,
      collection,
    } = this.state;
    const errors = {};

    if (!title) {
      errors.title = intl.formatMessage({ defaultMessage: 'This field is required' });
    }

    if (requirePassword && !password) {
      errors.password = intl.formatMessage({ defaultMessage: 'This field is required' });
    }

    if (!collection) {
      errors.collection = intl.formatMessage({ defaultMessage: 'You have to select a Collection' });
    }

    if (!isValidShareLinkSuffix(url.suffix)) {
      errors.url.suffix = intl.formatMessage({
        defaultMessage: 'Allowed characters for link suffix are: A-Z, a-z, 0-9, -, _',
      });
    }

    this.setState({ errors });

    if (Object.keys(errors).length > 0) {
      this.setState({
        view: (Object.keys(errors).includes('title') || Object.keys(errors).includes('collection')) ? null : 'settings',
      });
      return false;
    }

    return true;
  }

  handleChange(e) {
    const {
      name,
      value,
      checked,
      type,
    } = e.target;
    const {
      intl,
      statuses,
    } = this.props;

    if (name === 'fileStatus') {
      const newStatus = statuses.find(s => (s.id === value));
      if (newStatus) {
        this.setState({ fileStatus: newStatus });
      } else {
        this.setState({
          fileStatus: {
            value: '',
            label: intl.formatMessage({ defaultMessage: '-- no status --' }),
            color: null,
          },
        });
      }
    } else if (type === 'checkbox') {
      this.setState({ [name]: checked });
    } else {
      this.setState({ [name]: value });
    }
  }

  handleKeyPress(e) {
    e.persist();
    if (e.key === 'Enter') {
      e.preventDefault();
      this.handleSave();
    }
  }

  handleSave() {
    if (!this.validateForm()) {
      return;
    }

    const {
      url,
      title,
      description,
      password,
      requirePassword,
      requireLogin,
      requireEmail,
      requireName,
      collection,
      user,
      expirationDate,
      fileStatus,
      defaultFilesAccess,
      requireAcceptDocument,
    } = this.state;
    const {
      uploadPage,
      handleClose,
      notificationCallback,
    } = this.props;

    this.setState({ saving: true });

    const updatedUploadPage = {};
    updatedUploadPage.url = url;
    updatedUploadPage.title = title;
    updatedUploadPage.description = description;
    updatedUploadPage.require_password = requirePassword;
    updatedUploadPage.password = password;
    updatedUploadPage.require_login = requireLogin;
    updatedUploadPage.require_email = requireEmail;
    updatedUploadPage.require_name = requireName;
    updatedUploadPage.collection_uuid = collection.uuid;
    updatedUploadPage.user_uuid = user ? user.uuid : null;
    updatedUploadPage.expires_at = expirationDate;
    updatedUploadPage.status_id = fileStatus?.id;
    updatedUploadPage.default_files_access = defaultFilesAccess;
    updatedUploadPage.require_accept_document = requireAcceptDocument;

    if (uploadPage) {
      APIupdateUploadPage(uploadPage.uuid, updatedUploadPage)
        .then((response) => {
          if (response.data) {
            notificationCallback(response.data);
            handleClose();
          }
        })
        .catch((error) => {
          this.setState({
            errors: parseResponseErrors(error.response),
            saving: false,
          });
        });
    } else {
      APIcreateUploadPage(updatedUploadPage)
        .then((response) => {
          if (response.data) {
            notificationCallback(response.data);
            handleClose();
          }
        })
        .catch((error) => {
          this.setState({
            errors: parseResponseErrors(error.response),
            saving: false,
          });
        });
    }
  }

  handleNewCollection(collection) {
    const { errors } = this.state;

    if (errors.collection) {
      delete errors.collection;
    }
    this.setState({
      collection,
      errors,
    });
  }

  handleRemoveSelectedCollection() {
    this.setState({ collection: null });
  }

  handleSelectUser(userUuid) {
    const { users } = this.state;

    this.setState({ user: users.find(u => (u.uuid === userUuid)) || null });
  }

  changeUrlSuffix(e) {
    const { intl } = this.props;
    const { url } = this.state;

    url.suffix = e.target.value;
    this.setState({
      url,
      errors: {},
    });

    if (!isValidShareLinkSuffix(e.target.value)) {
      const { errors } = this.state;
      errors.url_suffix = intl.formatMessage({
        defaultMessage: 'Allowed characters for link suffix are: A-Z, a-z, 0-9, -, _',
      });
      this.setState({ errors });
    }
  }

  changeExpirationDate(expirationDate) {
    this.setState({ expirationDate });
  }

  handleChangeRequireAcceptDocument(e) {
    this.setState({ requireAcceptDocument: e.target.checked });
  }

  openCollectionSelectModal() {
    this.setState({ collectionSelectOpen: true });
  }

  closeCollectionSelectModal() {
    this.setState({ collectionSelectOpen: false });
  }

  resetView() {
    this.setState({ view: null });
  }

  toggleDropdown() {
    this.setState(prevState => ({
      dropdownOpen: !prevState.dropdownOpen,
    }));
  }

  changeDefaultFilesAccess(value) {
    this.toggleDropdown();
    this.setState({ defaultFilesAccess: value });
  }

  render() {
    const {
      url,
      title,
      description,
      collection,
      user,
      requireLogin,
      requireEmail,
      requireName,
      errors,
      saving,
      users,
      usersLoading,
      collectionSelectOpen,
      defaultFilesAccess,
      fileStatus,
      dropdownOpen,
      view,
    } = this.state;
    const {
      intl,
      uploadPage,
      handleClose,
    } = this.props;

    const urlIncorrect = Boolean(errors.url_suffix) || Boolean(errors['url.suffix']);
    const urlError = errors.url_suffix || errors['url.suffix'];
    const qrCodeUrl = `${url.prefix}${url.suffix}/qr`;

    const inheritText = intl.formatMessage({ defaultMessage: 'Inherit Collection access' });
    const publicText = intl.formatMessage({ defaultMessage: 'Visible to all users in your workspace' });
    const restrictedText = intl.formatMessage({ defaultMessage: 'Visible only to users and Teams you add' });

    return (
      <ErrorBoundary>
        <ModalHeader toggle={handleClose} className="modal-header-center">
          {uploadPage ? (
            intl.formatMessage({ defaultMessage: 'Edit guest upload page' })
          ) : (
            intl.formatMessage({ defaultMessage: 'New guest upload page' })
          )}
        </ModalHeader>

        <div className="modal-share modal-share-inline">
          {!view && (
            <ModalBody>
              <Form
                className="upload-page-form"
                onKeyPress={this.handleKeyPress}
              >
                <div className="share-link">
                  <div className="share-item">
                    <div className="user-avatar share-avatar">
                      <div className="avatar avatar-40 icon-avatar outline-avatar primary">
                        <i className="fas fa-arrow-up-from-bracket" />
                      </div>
                    </div>
                    <div className="share-item-body share-link-body">
                      <span className="share-item-details share-link-details">
                        {this.getAccessText()} {Parser(`${this.getExpirationText()}`)}
                      </span>
                    </div>
                    <div className="share-item-actions share-link-actions">
                      <Button color="link" id="linkOpenSettings" onClick={this.openSettings}>
                        <i className="far fa-cog" />
                      </Button>
                      <UncontrolledTooltip placement="bottom" target="linkOpenSettings">
                        {intl.formatMessage({ defaultMessage: 'Link settings' })}
                      </UncontrolledTooltip>
                    </div>
                  </div>
                  <div className="share-link-form">
                    <div className="share-link-inputs-wrapper">
                      <div>
                        <InputGroup>
                          <InputGroupAddon addonType="prepend">
                            <InputGroupText>{url.prefix}</InputGroupText>
                          </InputGroupAddon>
                          <Input
                            type="text"
                            name="shareLink"
                            id="inpShareLink"
                            defaultValue={url.suffix}
                            onChange={this.changeUrlSuffix}
                            invalid={urlIncorrect}
                          />
                        </InputGroup>
                        {!urlIncorrect && (
                          <ShareModalLinkCopy linkUrl={`${url.prefix}${url.suffix}`} />
                        )}
                      </div>
                      {!urlIncorrect && (
                        <>
                          <a href={qrCodeUrl} id="linkQRCode" className="btn btn-link btn-link-qr-code" download={`upload-${url.suffix}.png`}>
                            <i className="far fa-qrcode" />
                          </a>
                          <UncontrolledTooltip placement="bottom" target="linkQRCode">
                            {intl.formatMessage({ defaultMessage: 'Get QR Code' })}
                          </UncontrolledTooltip>
                        </>
                      )}
                    </div>
                    {urlIncorrect && (
                      <FormFeedback style={{ display: 'block' }}>
                        {urlError}
                      </FormFeedback>
                    )}
                  </div>
                </div>

                <Row form>
                  <Col>
                    <FormGroup>
                      <Label for="inpLogin">
                        {intl.formatMessage({ defaultMessage: 'Page title' })}
                      </Label>
                      <InputGroup>
                        <Input
                          type="text"
                          name="title"
                          id="inpTitle"
                          placeholder=""
                          defaultValue={title}
                          onChange={this.handleChange}
                          invalid={errors.title !== undefined}
                        />
                        {errors.title !== undefined && (
                          <FormFeedback>{errors.title}</FormFeedback>
                        )}
                      </InputGroup>
                    </FormGroup>
                  </Col>
                </Row>

                <Row form>
                  <Col>
                    <FormGroup className={classnames({ 'is-invalid': errors.description !== undefined })}>
                      <Label for="inpDescription">
                        {intl.formatMessage({ defaultMessage: 'Description' })}
                      </Label>
                      <Input
                        type="text"
                        name="description"
                        id="inpDescription"
                        placeholder={intl.formatMessage({ defaultMessage: 'Account description' })}
                        defaultValue={description}
                        onChange={this.handleChange}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row className="connect-collection" form>
                  <Col>
                    <FormGroup className={classnames({ 'is-invalid': errors.collection !== undefined })}>
                      <Label for="inpCollection">
                        {intl.formatMessage({ defaultMessage: 'Connect with Collection' })}
                      </Label>
                      {errors.collection !== undefined && (
                        <FormFeedback>{errors.collection}</FormFeedback>
                      )}
                      <Button
                        className="add-collection"
                        color="secondary"
                        onClick={this.openCollectionSelectModal}
                      >
                        {intl.formatMessage({ defaultMessage: 'Select or create Collection' })}
                      </Button>
                      {collection && (
                        <div className="tags-list">
                          <div className="tag">
                            <span className="tag-label">
                              {collection.name}
                            </span>
                            {/* eslint-disable jsx-a11y/click-events-have-key-events */}
                            <span
                              className="tag-remove"
                              title="Remove"
                              role="button"
                              tabIndex={-1}
                              onClick={this.handleRemoveSelectedCollection}
                            >
                              <i className="far fa-times" />
                            </span>
                            {/* eslint-enable jsx-a11y/click-events-have-key-events */}
                          </div>
                        </div>
                      )}
                    </FormGroup>
                    <CollectionSelectModal
                      modalOpen={collectionSelectOpen}
                      enableAddNew={userCan('collection.create')}
                      handleClose={this.closeCollectionSelectModal}
                      handleCollectionSelect={this.handleNewCollection}
                      smartCollectionsRestrict={false}
                      showSmartSection={false}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Label className="switch mb-3">
                      <div className="switch-details">
                        <span>
                          {intl.formatMessage({ defaultMessage: 'Default file access' })}
                        </span>
                      </div>
                      <div id="upload-page-files-access" className="access-type-select">
                        <Button
                          color="link"
                          onClick={(e) => e.preventDefault()}
                          size="sm"
                        >
                          {defaultFilesAccess === 'inherit' && intl.formatMessage({ defaultMessage: 'Inherit Collection access' })}
                          {defaultFilesAccess === 'open' && intl.formatMessage({ defaultMessage: 'Open' })}
                          {defaultFilesAccess === 'restricted' && intl.formatMessage({ defaultMessage: 'Restricted' })}
                          {' '}
                          <i className={`fas fa-${dropdownOpen ? 'chevron-up' : 'chevron-down'}`} />
                        </Button>
                      </div>
                    </Label>
                    <Popover
                      popperClassName="access-type-dropdown"
                      placement="bottom-start"
                      trigger="legacy"
                      isOpen={dropdownOpen}
                      target="upload-page-files-access"
                      boundariesElement="body"
                      delay={0}
                      fade={false}
                      hideArrow={true}
                      positionFixed={true}
                      offset={15}
                      toggle={this.toggleDropdown}
                    >
                      <PopoverBody>
                        <FormGroup check>
                          <Label className="access-type-item" check>
                            <Input
                              type="radio"
                              value="inherit"
                              checked={defaultFilesAccess === 'inherit'}
                              onChange={() => this.changeDefaultFilesAccess('inherit')}
                            />
                            {' '}
                            <h5 className="access-type-title">
                              {intl.formatMessage({ defaultMessage: 'Inherit Collection access' })}
                            </h5>
                            <p className="access-type-details">
                              {inheritText}
                            </p>
                          </Label>
                        </FormGroup>
                        <FormGroup check>
                          <Label className="access-type-item" check>
                            <Input
                              type="radio"
                              value="public"
                              checked={defaultFilesAccess === 'open'}
                              onChange={() => this.changeDefaultFilesAccess('open')}
                            />
                            {' '}
                            <h5 className="access-type-title">
                              {intl.formatMessage({ defaultMessage: 'Open' })}
                            </h5>
                            <p className="access-type-details">
                              {publicText}
                            </p>
                          </Label>
                        </FormGroup>
                        <FormGroup check>
                          <Label className="access-type-item" check>
                            <Input
                              type="radio"
                              value="restricted"
                              checked={defaultFilesAccess === 'restricted'}
                              onChange={() => this.changeDefaultFilesAccess('restricted')}
                            />
                            {' '}
                            <h5 className="access-type-title">
                              {intl.formatMessage({ defaultMessage: 'Restricted' })}
                            </h5>
                            <p className="access-type-details">
                              {restrictedText}
                            </p>
                          </Label>
                        </FormGroup>
                      </PopoverBody>
                    </Popover>
                  </Col>
                </Row>

                <Row className="connect-user" form>
                  <Col>
                    <FormGroup>
                      <Label for="inpUser">
                        {intl.formatMessage({ defaultMessage: 'Assign user to guest upload page' })}
                      </Label>
                      <AccessModalSelect
                        groups={[]}
                        users={users}
                        selectedUsers={user ? { [user.uuid]: 1 } : {}}
                        loading={usersLoading}
                        listType="users"
                        singleSelect={true}
                        handleAddItem={this.handleSelectUser}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </Form>
            </ModalBody>
          )}

          {view === 'settings' && (
            <ModalBody className="modal-body-settings">
              <div className="share-setting file-status-setting form-inline file-status-field">
                <FileStatusSelect
                  label={intl.formatMessage({ defaultMessage: 'Add a default file status' })}
                  defaultValue={fileStatus}
                  handleChange={this.handleChange}
                  scope="upload"
                />
              </div>
              <ShareModalExpirationDate
                {...this.state}
                handleChangeDate={this.changeExpirationDate}
              />
              <ModalRequireCheckbox
                label={intl.formatMessage({ defaultMessage: 'Require workspace login' })}
                name="requireLogin"
                checked={requireLogin}
                handleChange={this.handleChange}
              />
              <ShareModalPasswordProtect
                {...this.state}
                passwordError={!!errors.password}
                handleChangeRequirePassword={this.handleChange}
                handleChangePassword={this.handleChange}
              />
              <ModalRequireCheckbox
                label={intl.formatMessage({ defaultMessage: 'Require email' })}
                name="requireEmail"
                checked={requireEmail}
                handleChange={this.handleChange}
              />
              <ModalRequireCheckbox
                label={intl.formatMessage({ defaultMessage: 'Require name' })}
                name="requireName"
                checked={requireName}
                last={true}
                handleChange={this.handleChange}
              />
              {getSetting('upload_pages_require_accept_document') === '1' && (
                <ShareModalRequireAcceptDocument
                  {...this.state}
                  handleChange={this.handleChangeRequireAcceptDocument}
                />
              )}
            </ModalBody>
          )}
        </div>

        {!view && (
          <ModalFooter>
            <div className="buttons-group">
              <Button
                color="secondary"
                disabled={saving}
                onClick={handleClose}
              >
                {intl.formatMessage({ defaultMessage: 'Cancel' })}
              </Button>
              <Button
                color="primary"
                disabled={saving}
                onClick={this.handleSave}
              >
                <ProcessingSpinner show={saving} />
                {intl.formatMessage({ defaultMessage: 'Save' })}
              </Button>
            </div>
          </ModalFooter>
        )}

        {(view === 'settings' || view === 'error') && (
          <ModalFooter className="justify-l">
            <div className="buttons-group">
              <Button color="link" onClick={this.resetView}>
                <i className="far fa-long-arrow-left" />
                {intl.formatMessage({ defaultMessage: 'Back' })}
              </Button>
            </div>
          </ModalFooter>
        )}
      </ErrorBoundary>
    );
  }
}

const mapStateToProps = state => ({
  statuses: state.Files.statuses,
});

UploadPageEditModal.propTypes = {
  intl: PropTypes.object.isRequired,
  statuses: PropTypes.array.isRequired,
  handleClose: PropTypes.func.isRequired,
  uploadPage: PropTypes.object,
  notificationCallback: PropTypes.func,
};

UploadPageEditModal.defaultProps = {
  uploadPage: null,
  notificationCallback: () => { },
};

export default connect(mapStateToProps)(injectIntl(UploadPageEditModal));
